/**
 * Project & membership models for project-level scoping (Group H).
 * Mirror the API ProjectViewModel / ProjectMemberViewModel.
 */

/** Operational location of a project; drives onshore/offshore leadership roll-ups (ID 39). */
export type ProjectLocation = 'Onshore' | 'Offshore' | 'Both';

/** Admin all-projects roll-up filter: a specific location or all projects. */
export type ProjectLocationFilter = 'Onshore' | 'Offshore' | null;

/**
 * Where a project came from: created in QMS, synchronized from Capital Projects (EPPM),
 * or part of the curated Project Portfolio Dashboard list.
 */
export type ProjectSourceValue = 'Qms' | 'Eppm' | 'Portfolio' | 'Wells';

/** The protected QMS project that stores records not associated with any project. */
export const NO_PROJECT_ID = '5e1f0000-0000-4000-8000-000000000001';
export const NOT_APPLICABLE_PROJECT = 'N/A';

/** Human-readable label for each project source, used by the selector filter and card badges. */
export const PROJECT_SOURCE_LABELS: Record<ProjectSourceValue, string> = {
  Qms: 'QMS-only',
  Eppm: 'EPPM',
  Portfolio: 'Project Portfolio Dashboard',
  Wells: 'Wells',
};

export interface Project {
  id: string;
  code: string;
  name: string;
  description: string;
  businessUnit: string;
  location: ProjectLocation;
  archived: boolean;
  tags?: string[];
  members?: ProjectMember[];
  /** Read-only origin of the project; server-owned. */
  source?: ProjectSourceValue;
  /** Upstream status for EPPM-backed projects; null otherwise. */
  status?: string | null;
  /** False only on selector cards the current user may request access to. */
  canAccess?: boolean;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

/** A page of projects (mirrors the API ProjectPageViewModel). */
export interface ProjectPage {
  items: Project[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ProjectMember {
  id: string;
  projectId: string;
  userObjectId: string;
  displayName: string;
  email: string;
  projectRole: string;
  createdAt: string;
}
